import { formatDate } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';

import { IdoReportsService } from 'src/app/core/services/reports/ido/ido-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import {
  CONSTS,
  CSV_FILE_COLUMN_HEADING,
  FILE_NAME,
  GroupForecastFormGroup,
} from './group-forecast.constants';

@Component({
  selector: 'app-group-forecast',
  templateUrl: './group-forecast.component.html',
  styleUrls: ['./group-forecast.component.scss'],
  standalone: false,
})
export class GroupForecastComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: GroupForecastFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private dialogRef: MatDialogRef<GroupForecastComponent> = inject(
    MatDialogRef<GroupForecastComponent>,
  );
  private fb: FormBuilder = inject(FormBuilder);
  private reportUtils: ReportsUtils = inject(ReportsUtils);
  private idoReportsService: IdoReportsService = inject(IdoReportsService);
  private commonUtils: CommonUtils = inject(CommonUtils);

  readonly CONSTS = CONSTS;
  readonly SRC = IMAGE_SRC;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  groupForecastForm!: FormGroup;
  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;

  ngOnInit() {
    this.currDate = new Date();
    this.minDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 30,
    );
    this.maxDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() + 14,
    );

    this.groupForecastForm = this.fb.group({
      date: [this.currDate],
    });
  }

  clickExit() {
    this.commonUtils.openExitModal(this.groupForecastForm.dirty);
  }

  /**
   * Used to get report in CSV file format
   *
   * @memberof CollectedComponent
   */
  getReport() {
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          const formValue = this.groupForecastForm.value;

          const locationcode = user?.location;
          const date = this.commonUtils.getFormattedDate(formValue.date);

          const request = {
            locationcode,
            date,
          };

          return this.idoReportsService.getGroupForecastReport(request).pipe(
            take(1),
            map((data) => {
              const groupForecastvsActual =
                data?.response?.groupForecastvsActual || [];

              if (groupForecastvsActual.length) {
                const customHeader = [
                  [
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    'Group Forecast vs. Actual',
                    '',
                    '',
                    '',
                    '',
                    '',
                  ],
                  [
                    '',
                    'Group',
                    `${data?.response.group}`,
                    '',
                    '',
                    'Group Name',
                    `${data?.response.groupName}`,
                    '',
                    '',
                    '',
                    'Date',
                    `${data?.response.date}`,
                  ],
                ];
                const formatedDate = formatDate(
                  new Date(),
                  'MM-dd-yyyy',
                  'en-US',
                );
                const customFooter = [
                  [
                    'TOTAL',
                    '',
                    `${data?.response.totalforecastedDelStops}`,
                    `${data?.response.totalactualDelStops}`,
                    `${data?.response.totaldelStopsFrcstvsStopsDel}`,
                    `${data?.response.totalforecastedDelPkgs}`,
                    `${data?.response.totalactualDelPkgs}`,
                    `${data?.response.totaldelPkgsFrcstvsPkgsActual}`,
                    `${data?.response.totalNumofPkgsDelNotFrcst}`,
                    `${data?.response.totalPercentofPkgsDelNotFrcst}`,
                    `${data?.response.totalNumofFrcstPkgsNotFound}`,
                    `${data?.response.totalpercentofFrcstPkgsNotFound}`,
                  ],
                  ['Printed:', `${formatedDate}`],
                ];
                this.reportUtils.exportCSVReport(
                  groupForecastvsActual,
                  FILE_NAME,
                  customHeader,
                  customFooter,
                  CSV_FILE_COLUMN_HEADING,
                );
              } else {
                this.commonUtils.updateBannerType(
                  BANNER_TYPE.NO_DATA_AVAILABLE,
                );
              }
              this.dialogRef.close();
            }),
          );
        }),
      )
      .subscribe();
  }
}
